import { useEffect, useState } from "preact/hooks";
import { REVEAL_FLIP_MS, REVEAL_HALF_MS } from "../effects/reveal";
import type { Mark } from "../game/scoring";

type Props = {
  letter: string | null;
  mark?: Mark | undefined;
  /** Currently-active letter slot in the active row (cursor or its mirror). */
  active?: boolean;
  /** Pivot letter highlight (single center cell). */
  pivot?: boolean;
  /** When set, this cell runs the outside-in flip-reveal: rotateX with
      a delay of `revealDelayMs`, and the mark class is held back until
      the 90° midpoint so the colour swap is hidden behind the edge-on
      tile. Undefined = no animation; mark applies immediately. */
  revealDelayMs?: number | undefined;
};

export function Cell({
  letter,
  mark,
  active = false,
  pivot = false,
  revealDelayMs,
}: Props) {
  // While the flip's first half is running, hide the mark. After the
  // 90° midpoint we let the mark class through. Initial-mount: if a
  // delay was passed, suppress the mark immediately so the very first
  // paint shows the pre-submit look, even before the timer fires.
  const [maskMark, setMaskMark] = useState(revealDelayMs != null);
  useEffect(() => {
    if (revealDelayMs == null) {
      setMaskMark(false);
      return;
    }
    setMaskMark(true);
    const t = window.setTimeout(
      () => setMaskMark(false),
      revealDelayMs + REVEAL_HALF_MS,
    );
    return () => window.clearTimeout(t);
  }, [revealDelayMs]);

  const effectiveMark = maskMark ? undefined : mark;
  const cls = [
    "cell",
    effectiveMark ? `cell--${effectiveMark}` : "",
    active ? "cell--active" : "",
    pivot ? "cell--pivot" : "",
    letter ? "cell--filled" : "",
    revealDelayMs != null ? "cell--revealing" : "",
  ]
    .filter(Boolean)
    .join(" ");
  // Custom CSS properties on inline style aren't in Preact's typed
  // CSSProperties, but the style attribute happily accepts any string
  // record at runtime — cast through Record<string, string>.
  const style: Record<string, string> | undefined =
    revealDelayMs != null
      ? {
          "--reveal-delay": `${revealDelayMs}ms`,
          "--reveal-duration": `${REVEAL_FLIP_MS}ms`,
        }
      : undefined;
  return (
    <span class={cls} style={style}>
      {letter ? letter.toUpperCase() : ""}
    </span>
  );
}
